import { axiosBff, AxiosResponse } from '@/plugins/axios';
import { FormValues } from '@cnamts/form-builder/src/functions/getFormValues/types';
import { FieldList } from '@cnamts/form-builder/src/components/FormFieldList/types';
import { QrpQuestionnaireModel, QuestionnaireModel } from '@/services/questionnaire/types';
import { buildUrlWithSiret, getCurrentSiret } from '@/functions/buildUrlWithSiret';

class QuestionnaireService {

	async getQuestionnaires(): Promise<AxiosResponse<string[]>> {
		return axiosBff.get('/api/questionnaires');
	}

	async getQrpQuestionnaire(reponseId: string): Promise<AxiosResponse<QrpQuestionnaireModel>> {
		const url = buildUrlWithSiret(`/api/qrp_questionnaires/${reponseId}`);
		return axiosBff.get(url);
	}

	async getForm(questionnaireId: string, responseUnit: string | null, step: string | null): Promise<AxiosResponse<string[]>> {
		const url = buildUrlWithSiret(`/api/questionnaires/${questionnaireId}/form`, {
			reponse_unit: responseUnit,
			etape: step
		});
		return axiosBff.get(url);
	}

	async getRecap(questionnaireId: string, responseUnit: string): Promise<AxiosResponse<FieldList[]>> {
		const url = buildUrlWithSiret(`/api/questionnaires/${questionnaireId}/reponses/units/${responseUnit}/recap`);
		return axiosBff.get(url);
	}

	async postForm(questionnaireId: string, responses: FormValues | null, responseUnit: string | null, step: string | null, config?: { skipInstanceNotification?: boolean }): Promise<AxiosResponse<string[]>> {
		const url = buildUrlWithSiret(`/api/questionnaires/${questionnaireId}/form`, {
			reponse_unit: responseUnit,
			etape: step
		});
		return axiosBff.post(url, responses, config);
	}

	async submitResponses(questionnaireId: string, responses: FormValues, responseUnit: string | null, step: string | null, config?: { skipInstanceNotification?: boolean }): Promise<AxiosResponse<string[]>> {
		let url: string;

		if (!responseUnit) {
			url = buildUrlWithSiret(`/api/questionnaires/${questionnaireId}/reponses`);
			return axiosBff.post(url, responses, config);
		}

		url = buildUrlWithSiret(`/api/questionnaires/${questionnaireId}/reponses/${responseUnit}`, {
			etape: step
		});

		return axiosBff.put(url, responses, config);
	}

	async validateQuestionnaire(questionnaireId: string): Promise<AxiosResponse<string[]>> {
		const url = buildUrlWithSiret(`/api/qrp_questionnaires/${questionnaireId}/validate`);
		return axiosBff.post(url, {});
	}

	async getPiece(idPiece: string, sendMail = 'false'): Promise<AxiosResponse<Blob>> {
		const url = buildUrlWithSiret(`/api/downloadAttachment/file/${idPiece}`, { sendMail });
		return axiosBff.get(url, { responseType: 'blob' });
	}

	async getPieceQuestionnaire(idQuestionnaire: string): Promise<AxiosResponse<Blob>> {
		const url = buildUrlWithSiret(`/api/download/qrp_questionnaire/valide/${idQuestionnaire}`);
		return axiosBff.get(url, {
			responseType: 'blob',
			headers: { 'Accept': 'application/octet-stream' }
		});
	}

	async getPieceDirect(fileReference: string): Promise<AxiosResponse<Blob>> {
		const url = buildUrlWithSiret(`/api/downloadDirect/file/piece/${fileReference}`);
		return axiosBff.get(url, {
			responseType: 'blob',
			headers: { 'Accept': 'application/octet-stream' }
		});
	}

	async uploadFile(file: File, uploadRoute: string): Promise<AxiosResponse<string[]>> {
		const url = buildUrlWithSiret(`/api/files/upload/${uploadRoute}`);
		const formData = new FormData();
		formData.append('filename', file);
		return axiosBff.post(url, formData, {
			headers: { 'Content-Type': 'multipart/form-data' }
		});
	}

async getAllEmptyRecap(dossierId: string, siret?: string): Promise<AxiosResponse<Blob>> {
	const effectiveSiret = siret || getCurrentSiret();
	const url = buildUrlWithSiret(`/api/acteur/download/dossier/files/${dossierId}`, {
		siret: effectiveSiret
	});
	return axiosBff.get(url, { responseType: 'blob' });
}

	async getAllPieces(dossierId: string, sendMail = 'false'): Promise<AxiosResponse<Blob>> {
		const url = buildUrlWithSiret(`/api/download/all/files/dossier/${dossierId}`, { sendMail });
		return axiosBff.get(url, {
			responseType: 'blob',
			headers: { 'Accept': 'application/octet-stream' }
		});
	}

	async getIdQuestionnaireTemoin(): Promise<AxiosResponse<QuestionnaireModel>> {
		return axiosBff.get('/api/questionnaire-temoin');
	}

	async getDocumentProvisoire(questionnaireId: string, responseUnit: string): Promise<AxiosResponse<Blob>> {
		const url = buildUrlWithSiret(`/api/pdf/document-provisoire/${questionnaireId}/${responseUnit}`);
		return axiosBff.get(url, {
			responseType: 'blob',
			headers: { 'Accept': 'application/octet-stream' }
		});
	}
}

export const questionnaireService = new QuestionnaireService();
